import React, { useEffect, useState } from 'react';
import { createPVReception, getPVReception, updateReserve, savePvSignature, getPvSignatureUrl, deletePvSignature } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { SignaturePad } from '../SignaturePad';
import { CheckCircle2, ClipboardCheck, Plus, Trash2, AlertCircle, PenLine, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

export const PVReceptionTab = ({ dossier, onUpdated }) => {
    const [pv, setPv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [signDialog, setSignDialog] = useState(null); // { role, name }
    const [signDataUrl, setSignDataUrl] = useState(null);
    const [signing, setSigning] = useState(false);
    const [form, setForm] = useState({
        date_reception: format(new Date(), 'yyyy-MM-dd'),
        avec_reserves: false,
        notes: '',
        nom_client_signataire: '',
        nom_uneed_signataire: '',
        reserves: []
    });

    const load = async () => {
        setLoading(true);
        try {
            const res = await getPVReception(dossier.id);
            setPv(res.data || null);
        } catch {
            setPv(null);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [dossier.id]);
    const addReserve = () => {
        setForm((p) => ({ ...p, avec_reserves: true, reserves: [...p.reserves, { _uid: `r-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, description: '', action_corrective: '', delai_resolution: '' }] }));
    };
    const removeReserve = (idx) => {
        setForm((p) => ({ ...p, reserves: p.reserves.filter((_, i) => i !== idx) }));
    };
    const updateReserveField = (idx, field, value) => {
        setForm((p) => ({ ...p, reserves: p.reserves.map((r, i) => i === idx ? { ...r, [field]: value } : r) }));
    };

    const handleCreate = async () => {
        if (!form.date_reception) {
            toast.error('Date de réception requise');
            return;
        }
        if (form.avec_reserves && form.reserves.length === 0) {
            toast.error('Ajoutez au moins une réserve ou décochez "avec réserves"');
            return;
        }
        // Validate reserves
        for (const r of form.reserves) {
            if (!r.description.trim()) {
                toast.error('Chaque réserve doit avoir une description');
                return;
            }
        }
        try {
            const payload = {
                ...form,
                dossier_id: dossier.id,
                date_reception: new Date(form.date_reception).toISOString(),
                reserves: form.avec_reserves ? form.reserves : []
            };
            await createPVReception(dossier.id, payload);
            toast.success('PV de réception créé');
            setCreateOpen(false);
            setForm({
                date_reception: format(new Date(), 'yyyy-MM-dd'),
                avec_reserves: false,
                notes: '',
                nom_client_signataire: '',
                nom_uneed_signataire: '',
                reserves: []
            });
            load();
            onUpdated && onUpdated();
        } catch (e) {
            const detail = e.response?.data?.detail;
            let msg;
            if (Array.isArray(detail)) {
                msg = detail.map((d) => d.msg || JSON.stringify(d)).join(' • ');
            } else if (typeof detail === 'string') {
                msg = detail;
            } else {
                msg = 'Erreur création PV';
            }
            toast.error(msg);
        }
    };

    const toggleReserveStatus = async (reserveId, currentStatut) => {
        const newStatut = currentStatut === 'levee' ? 'ouverte' : 'levee';
        try {
            await updateReserve(reserveId, { statut: newStatut });
            toast.success(newStatut === 'levee' ? 'Réserve levée' : 'Réserve ré-ouverte');
            load();
        } catch {
            toast.error('Erreur');
        }
    };

    const openSignDialog = (role) => {
        setSignDialog({
            role,
            name: role === 'client' ? (pv?.nom_client_signataire || '') : (pv?.nom_uneed_signataire || ''),
        });
        setSignDataUrl(null);
    };

    const saveSignature = async () => {
        if (!signDataUrl) {
            toast.error('Veuillez signer dans la zone prévue');
            return;
        }
        if (!signDialog?.name?.trim()) {
            toast.error('Nom du signataire requis');
            return;
        }
        setSigning(true);
        try {
            await savePvSignature(pv.id, {
                role: signDialog.role,
                image: signDataUrl,
                signataire_nom: signDialog.name.trim(),
            });
            toast.success('Signature enregistrée');
            setSignDialog(null);
            setSignDataUrl(null);
            await load();
            onUpdated && onUpdated();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur signature');
        } finally {
            setSigning(false);
        }
    };

    const removeSignature = async (role) => {
        if (!window.confirm(`Effacer la signature ${role === 'client' ? 'client' : 'UNeedServices'} ?`)) return;
        try {
            await deletePvSignature(pv.id, role);
            toast.success('Signature effacée');
            load();
            onUpdated && onUpdated();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    return (
        <div className="bg-white border border-[#E4E4E7] p-6 rounded-sm space-y-4" data-testid="pv-reception-tab">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-[#16A34A]" />
                    PV de réception
                </h3>
                {!pv && (
                    <Button
                        onClick={() => setCreateOpen(true)}
                        className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-sm"
                        data-testid="create-pv-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" /> Créer le PV
                    </Button>
                )}
            </div>

            {loading ? (
                <p className="text-sm text-[#52525B]">Chargement...</p>
            ) : !pv ? (
                <div className="text-center py-6 text-[#52525B]">
                    <ClipboardCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun PV de réception pour ce chantier</p>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="bg-[#FAFAFA] p-4 rounded-sm">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="field-label">Date de réception</p>
                                <p className="font-medium">{format(new Date(pv.date_reception), 'dd MMMM yyyy', { locale: fr })}</p>
                            </div>
                            <div>
                                <p className="field-label">Statut</p>
                                <p className={`font-medium ${pv.statut === 'signe' ? 'text-green-700' : pv.avec_reserves ? 'text-orange-600' : 'text-[#52525B]'}`}>
                                    {pv.statut === 'signe' ? 'Signé par les deux parties' :
                                        pv.avec_reserves ? `Avec ${pv.reserves?.length || 0} réserve(s)` : 'Sans réserves'}
                                </p>
                            </div>
                        </div>
                        {pv.notes && (
                            <div className="mt-3 pt-3 border-t border-[#E4E4E7]">
                                <p className="field-label">Notes</p>
                                <p className="text-sm whitespace-pre-wrap">{pv.notes}</p>
                            </div>
                        )}
                    </div>

                    {/* Signature blocks */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {['client', 'uneed'].map((role) => {
                            const hasSig = pv[`has_signature_${role}`] || pv[`signature_${role}_path`];
                            const nomKey = role === 'client' ? 'nom_client_signataire' : 'nom_uneed_signataire';
                            const dateKey = `date_signature_${role}`;
                            return (
                                <div key={role} className="border border-[#E4E4E7] rounded-sm p-4 bg-white" data-testid={`pv-signature-${role}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-semibold text-[#0A0A0A]">
                                            Signataire {role === 'client' ? 'client' : 'UNeedServices'}
                                        </p>
                                        {hasSig && (
                                            <button
                                                onClick={() => removeSignature(role)}
                                                className="text-xs text-[#DC2626] hover:underline flex items-center gap-1"
                                                data-testid={`pv-signature-${role}-delete`}
                                            >
                                                <RotateCcw className="w-3 h-3" /> Refaire
                                            </button>
                                        )}
                                    </div>
                                    {pv[nomKey] && <p className="text-sm font-medium">{pv[nomKey]}</p>}
                                    {pv[dateKey] && (
                                        <p className="text-xs text-[#52525B]">
                                            Signé le {format(new Date(pv[dateKey]), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                                        </p>
                                    )}
                                    {hasSig ? (
                                        <img
                                            src={`${getPvSignatureUrl(pv.id, role)}?t=${pv[dateKey] || ''}`}
                                            alt={`Signature ${role}`}
                                            className="mt-2 w-full h-32 object-contain bg-white border border-[#E4E4E7] rounded-sm"
                                            data-testid={`pv-signature-${role}-img`}
                                        />
                                    ) : (
                                        <Button
                                            type="button"
                                            onClick={() => openSignDialog(role)}
                                            variant="outline"
                                            className="mt-3 w-full rounded-sm border-[#E85A24] text-[#E85A24] hover:bg-[#E85A24] hover:text-white"
                                            data-testid={`pv-signature-${role}-btn`}
                                        >
                                            <PenLine className="w-4 h-4 mr-2" /> Signer ici
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {pv.reserves && pv.reserves.length > 0 && (
                        <div>
                            <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 text-orange-500" /> Réserves ({pv.reserves.length})
                            </h4>
                            <div className="border border-[#E4E4E7] rounded-sm divide-y divide-[#E4E4E7]">
                                {pv.reserves.map((r) => (
                                    <div key={r.id} className="p-3 text-sm" data-testid={`reserve-row-${r.id}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium">{r.description}</p>
                                                {r.action_corrective && (
                                                    <p className="text-xs text-[#52525B] mt-1">
                                                        Action : {r.action_corrective}
                                                    </p>
                                                )}
                                                {r.delai_resolution && (
                                                    <p className="text-xs text-[#52525B] mt-0.5">
                                                        Délai : {format(new Date(r.delai_resolution), 'dd/MM/yyyy', { locale: fr })}
                                                    </p>
                                                )}
                                            </div>
                                            <Button
                                                variant={r.statut === 'levee' ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => toggleReserveStatus(r.id, r.statut)}
                                                className={`rounded-sm shrink-0 ${r.statut === 'levee' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
                                                data-testid={`reserve-toggle-${r.id}`}
                                            >
                                                {r.statut === 'levee' ? <CheckCircle2 className="w-4 h-4 mr-1" /> : null}
                                                {r.statut === 'levee' ? 'Levée' : 'Marquer levée'}
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] sm:w-auto max-h-[85vh] overflow-y-auto" data-testid="pv-dialog">
                    <DialogHeader>
                        <DialogTitle>Créer le PV de réception</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="field-label">Date de réception *</Label>
                                <Input
                                    type="date"
                                    value={form.date_reception}
                                    onChange={(e) => setForm((p) => ({ ...p, date_reception: e.target.value }))}
                                    className="h-10 rounded-sm mt-1 w-full"
                                    data-testid="pv-date-input"
                                />
                            </div>
                            <div className="flex items-end sm:pb-2">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.avec_reserves}
                                        onChange={(e) => setForm((p) => ({ ...p, avec_reserves: e.target.checked }))}
                                        className="h-4 w-4"
                                        data-testid="pv-avec-reserves"
                                    />
                                    Avec réserves
                                </label>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="field-label">Signataire client</Label>
                                <Input
                                    value={form.nom_client_signataire}
                                    onChange={(e) => setForm((p) => ({ ...p, nom_client_signataire: e.target.value }))}
                                    placeholder="Prénom Nom"
                                    className="h-10 rounded-sm mt-1"
                                />
                            </div>
                            <div>
                                <Label className="field-label">Signataire UNeedServices</Label>
                                <Input
                                    value={form.nom_uneed_signataire}
                                    onChange={(e) => setForm((p) => ({ ...p, nom_uneed_signataire: e.target.value }))}
                                    placeholder="Prénom Nom"
                                    className="h-10 rounded-sm mt-1"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="field-label">Notes générales</Label>
                            <Textarea
                                rows={3}
                                value={form.notes}
                                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                                className="rounded-sm mt-1"
                                placeholder="Notes complémentaires..."
                            />
                        </div>

                        {form.avec_reserves && (
                            <div className="border-t border-[#E4E4E7] pt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-semibold text-sm flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4 text-orange-500" /> Réserves
                                    </h4>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addReserve}
                                        className="rounded-sm"
                                        data-testid="pv-add-reserve-btn"
                                    >
                                        <Plus className="w-4 h-4 mr-1" /> Ajouter
                                    </Button>
                                </div>
                                {form.reserves.length === 0 ? (
                                    <p className="text-sm text-[#52525B] italic">Aucune réserve ajoutée</p>
                                ) : (
                                    <div className="space-y-3">
                                        {form.reserves.map((r, idx) => (
                                            <div key={r._uid || `res-${idx}`} className="border border-[#E4E4E7] p-3 rounded-sm space-y-2 relative">
                                                <button
                                                    onClick={() => removeReserve(idx)}
                                                    className="absolute top-2 right-2 text-[#DC2626] hover:bg-red-50 rounded-sm p-1"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                                <div>
                                                    <Label className="field-label text-[10px]">Description *</Label>
                                                    <Textarea
                                                        rows={2}
                                                        value={r.description}
                                                        onChange={(e) => updateReserveField(idx, 'description', e.target.value)}
                                                        className="rounded-sm mt-1 text-sm"
                                                        placeholder="Décrivez la réserve..."
                                                        data-testid={`reserve-desc-${idx}`}
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                    <div>
                                                        <Label className="field-label text-[10px]">Action corrective</Label>
                                                        <Input
                                                            value={r.action_corrective}
                                                            onChange={(e) => updateReserveField(idx, 'action_corrective', e.target.value)}
                                                            placeholder="Ex : Reprise peinture"
                                                            className="h-9 rounded-sm mt-1 text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="field-label text-[10px]">Délai résolution</Label>
                                                        <Input
                                                            type="date"
                                                            value={r.delai_resolution}
                                                            onChange={(e) => updateReserveField(idx, 'delai_resolution', e.target.value)}
                                                            className="h-9 rounded-sm mt-1 text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setCreateOpen(false)} className="w-full sm:w-auto">Annuler</Button>
                        <Button
                            onClick={handleCreate}
                            className="bg-[#16A34A] hover:bg-[#15803D] text-white w-full sm:w-auto"
                            data-testid="pv-create-submit"
                        >
                            Créer le PV
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={!!signDialog} onOpenChange={(o) => { if (!o) { setSignDialog(null); setSignDataUrl(null); } }}>
                <DialogContent className="max-w-lg w-[calc(100vw-1rem)] sm:w-auto" data-testid="pv-sign-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            Signature {signDialog?.role === 'client' ? 'client' : 'UNeedServices'}
                        </DialogTitle>
                    </DialogHeader>
                    {signDialog && (
                        <div className="space-y-4">
                            <div>
                                <Label className="field-label">Nom du signataire *</Label>
                                <Input
                                    value={signDialog.name}
                                    onChange={(e) => setSignDialog((p) => ({ ...p, name: e.target.value }))}
                                    placeholder="Prénom Nom"
                                    className="h-10 rounded-sm mt-1"
                                    data-testid="pv-sign-name"
                                />
                            </div>
                            <div>
                                <Label className="field-label">Tracer la signature *</Label>
                                <SignaturePad
                                    onChange={setSignDataUrl}
                                    height={180}
                                    testId="pv-sign-pad"
                                />
                                <p className="text-xs text-[#52525B] mt-1">
                                    Utilisez la souris, le pavé tactile ou l&apos;écran tactile pour signer dans la zone.
                                </p>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => { setSignDialog(null); setSignDataUrl(null); }} disabled={signing} className="w-full sm:w-auto">Annuler</Button>
                        <Button
                            onClick={saveSignature}
                            disabled={signing || !signDataUrl}
                            className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white w-full sm:w-auto"
                            data-testid="pv-sign-submit"
                        >
                            {signing ? 'Enregistrement...' : 'Valider la signature'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
